import { GameName, GameState, GameTime, GameWaitState, Role, TicketStatus } from '../net/enums';
import { beginMessage, readMessage } from '../net/message';
import { MessageTag } from '../net/messageTags';
import type { BinaryWriter } from '../net/BinaryWriter';
import { writeGameInfoModel, type GameInfoModel } from '../net/models/gameInfo';
import { writePlayerModel, type PlayerModel } from '../net/models/player';
import { writeProfileModel, type ProfileModel } from '../net/models/profileModel';
import type { ITransport, TransportAvailabilityInfo, TransportCloseInfo, TransportConnectionInfo } from '../net/transport/ITransport';
import { shouldEnforceIosOnly } from '../platform/telegram';

/**
 * Canonical pages captured by the web/Godot visual-parity harness. Keep these names stable: the
 * capture script, screenshot filenames, and Godot fixture manifest all use the same slugs.
 */
export const VISUAL_FIXTURE_SLUGS = [
  'login',
  'login-forgot',
  'register-referral',
  'register-form',
  'main-menu',
  'main-menu-waiting',
  'online-list',
  'profile',
  'payment',
  'ticket-new',
  'ticket-detail',
  'create-blackjack',
  'create-hokm',
  'create-backgammon',
  'search-empty',
  'search-populated',
  'ready-owner',
  'ready-joiner',
  'reconnect',
] as const;

export type VisualFixtureSlug = (typeof VISUAL_FIXTURE_SLUGS)[number];

const VISUAL_FIXTURE_SET = new Set<string>(VISUAL_FIXTURE_SLUGS);

/**
 * Resolve a requested fixture only inside a Vite development build served from loopback. Merely
 * adding `?visual-fixture=...` to a hosted preview/production URL can never bypass Telegram/iOS
 * authentication or replace the real WebSocket transport.
 */
export function resolveVisualFixtureSlug(
  search: string,
  hostname: string,
  isDevBuild: boolean,
): VisualFixtureSlug | null {
  if (!isDevBuild || shouldEnforceIosOnly(hostname)) return null;
  const requested = new URLSearchParams(search).get('visual-fixture')?.trim().toLowerCase();
  return requested && VISUAL_FIXTURE_SET.has(requested) ? requested as VisualFixtureSlug : null;
}

type MessageHandler = (data: Uint8Array, connectionId: number) => void;
type CloseHandler = (info: TransportCloseInfo) => void;
type ErrorHandler = (error: unknown) => void;

/**
 * An in-memory protocol peer for deterministic screenshots. It consumes the same framed C2S bytes
 * that production sends and returns correctly framed S2C packets, so scene decoding and packet
 * subscriptions remain exercised. It is instantiated only after resolveVisualFixtureSlug succeeds.
 */
export class VisualFixtureTransport implements ITransport {
  private open = false;
  private currentConnectionId: number | null = null;
  private nextConnectionId = 0;
  private pendingFrames = 0;
  private dispatchError: unknown = null;
  private waiting: boolean;
  private readonly openHandlers = new Set<(info: TransportConnectionInfo) => void>();
  private readonly messageHandlers = new Set<MessageHandler>();
  private readonly closeHandlers = new Set<CloseHandler>();
  private readonly errorHandlers = new Set<ErrorHandler>();
  private readonly idleWaiters = new Set<() => void>();

  constructor(readonly slug: VisualFixtureSlug) {
    this.waiting = slug === 'main-menu-waiting';
  }

  get isOpen(): boolean {
    return this.open;
  }

  get isReady(): boolean {
    return this.open;
  }

  get connectionId(): number | null {
    return this.currentConnectionId;
  }

  get protocol(): string {
    return 'fixture';
  }

  async connect(url: string): Promise<TransportConnectionInfo> {
    void url;
    if (this.open && this.currentConnectionId !== null) {
      return { connectionId: this.currentConnectionId, protocol: this.protocol };
    }
    this.open = true;
    this.currentConnectionId = ++this.nextConnectionId;
    const info = { connectionId: this.currentConnectionId, protocol: this.protocol };
    for (const handler of [...this.openHandlers]) handler(info);
    return info;
  }

  send(data: Uint8Array): void {
    if (!this.open) throw new Error('Visual fixture transport is closed');
    const { tag, reader } = readMessage(data);

    switch (tag) {
      case MessageTag.LoadLogin:
        reader.getString();
        this.enqueue(loadLoginFrame());
        break;
      case MessageTag.Login: {
        const username = reader.getString() || FIXTURE_PLAYER.username;
        reader.getString(); // password
        reader.getShort(); // platform
        reader.getString(); // version
        this.enqueue(loginFrame(username));
        break;
      }
      case MessageTag.CheckParent:
        reader.getString();
        this.enqueue(checkParentFrame());
        break;
      case MessageTag.Register:
        this.enqueue(beginMessage(MessageTag.Register).putBool(true).toUint8Array());
        break;
      case MessageTag.ForgotPassword:
        reader.getString();
        this.enqueue(serverMessageFrame('لینک بازیابی رمز عبور ارسال شد.'));
        break;
      case MessageTag.Status:
        this.enqueue(mainMenuFrame(this.waiting));
        break;
      case MessageTag.NewGame:
        this.waiting = true;
        this.enqueue(mainMenuFrame(true));
        break;
      case MessageTag.CanselGame:
        this.waiting = false;
        this.enqueue(mainMenuFrame(false));
        break;
      case MessageTag.Telegram:
        this.enqueue(
          beginMessage(MessageTag.Telegram)
            .putString('https://t.me/pardisgame')
            .toUint8Array(),
        );
        break;
      case MessageTag.OnlineList:
        this.enqueue(onlineListFrame());
        break;
      case MessageTag.GetProfile:
        this.enqueue(profileFrame());
        break;
      case MessageTag.Cash:
        this.enqueue(cashFrame());
        break;
      case MessageTag.AgentList:
        this.enqueue(agentListFrame());
        break;
      case MessageTag.TransactionList:
        this.enqueue(transactionListFrame());
        break;
      case MessageTag.AgentRequest:
        this.enqueue(beginMessage(MessageTag.AgentRequest).putString('ACE-۷۲۴۵').toUint8Array());
        break;
      case MessageTag.GetNewGameConfigs:
        this.enqueue(newGameConfigFrame(reader.getShort() as GameName));
        break;
      case MessageTag.SearchGame:
        this.enqueue(searchGameFrame(this.slug === 'search-empty'));
        break;
      case MessageTag.Ticket:
        this.enqueue(ticketFrame(this.slug === 'ticket-detail'));
        break;
      case MessageTag.Reconnect:
        reader.getString();
        this.enqueue(
          beginMessage(MessageTag.Reconnect)
            .putBool(true)
            .putBool(false)
            .toUint8Array(),
        );
        break;
      case MessageTag.SaveAvatar:
      case MessageTag.SaveProfile:
      case MessageTag.ChangePassword:
      case MessageTag.TicketSave:
      case MessageTag.RecheckPayments:
      case MessageTag.RequestPayback:
      case MessageTag.CancelPayback:
        this.enqueue(serverMessageFrame('اطلاعات با موفقیت ثبت شد.'));
        break;
      default:
        // Buttons remain interactive in fixture mode. Actions without a visual response are accepted
        // as no-ops, matching the many production C2S packets whose reply is a later room transition.
        break;
    }
  }

  close(code = 1000, reason = '', local = true): void {
    if (!this.open) return;
    const connectionId = this.currentConnectionId ?? 0;
    this.open = false;
    this.currentConnectionId = null;
    const info: TransportCloseInfo = { code, reason, local, connectionId, current: true };
    for (const handler of [...this.closeHandlers]) handler(info);
  }

  probe(): void {}

  onOpen(handler: (info: TransportConnectionInfo) => void): void {
    this.openHandlers.add(handler);
  }

  onMessage(handler: MessageHandler): void {
    this.messageHandlers.add(handler);
  }

  onClose(handler: CloseHandler): void {
    this.closeHandlers.add(handler);
  }

  onAvailability(_handler: (info: TransportAvailabilityInfo) => void): void {}

  onError(handler: ErrorHandler): void {
    this.errorHandlers.add(handler);
  }

  /** Reveal phase two of Register through the real CheckParent response decoder. */
  revealRegisterForm(): void {
    this.enqueue(checkParentFrame());
  }

  /** Enter Ready through the same IsReady/WaitReady packet used by the production global handler. */
  showReady(isOwner: boolean): void {
    this.enqueue(readyFrame(isOwner));
  }

  /** Wait until every response triggered by scene onEnter (including nested requests) is dispatched. */
  async waitForIdle(): Promise<void> {
    while (this.pendingFrames > 0) {
      await new Promise<void>((resolve) => this.idleWaiters.add(resolve));
    }
    if (this.dispatchError) throw this.dispatchError;
  }

  private enqueue(frame: Uint8Array): void {
    this.pendingFrames++;
    queueMicrotask(() => {
      try {
        const connectionId = this.currentConnectionId;
        if (connectionId === null) return;
        for (const handler of [...this.messageHandlers]) handler(frame, connectionId);
      } catch (error) {
        this.dispatchError = error;
        for (const handler of [...this.errorHandlers]) handler(error);
      } finally {
        this.pendingFrames--;
        if (this.pendingFrames === 0) {
          for (const resolve of [...this.idleWaiters]) resolve();
          this.idleWaiters.clear();
        }
      }
    });
  }
}

const FIXTURE_PLAYER: PlayerModel = {
  userId: 900_724n,
  username: 'سارا_آس',
  avatar: 7,
};

const FIXTURE_OPPONENT: PlayerModel = {
  userId: 900_811n,
  username: 'آرمان_شاه',
  avatar: 13,
};

function loadLoginFrame(): Uint8Array {
  return beginMessage(MessageTag.LoadLogin)
    .putString('')
    .putBool(false)
    .toUint8Array();
}

function loginFrame(username: string): Uint8Array {
  const writer = beginMessage(MessageTag.Login).putBool(true);
  writePlayerModel(writer, { ...FIXTURE_PLAYER, username });
  return writer
    .putLong(12_750_000n)
    .putInt(128)
    .putString('visual-fixture-reconnect-token')
    .toUint8Array();
}

function checkParentFrame(): Uint8Array {
  return beginMessage(MessageTag.CheckParent).putLong(325n).toUint8Array();
}

function mainMenuFrame(waiting = false): Uint8Array {
  return beginMessage(MessageTag.MainMenu)
    .putInt(128)
    .putLong(12_750_000n)
    .putBool(waiting)
    .putString('خوش آمدیدکاربران گرامی 🌹  نسخه مخصوص آیفون از طریق کانال تلگرام در دسترس شما قرار گرفته است.')
    .putInt(FIXTURE_PLAYER.avatar)
    .toUint8Array();
}

function onlineListFrame(): Uint8Array {
  return beginMessage(MessageTag.OnlineList)
    .putStringArray([
      'سارا_آس',
      'آرمان_شاه',
      'ماه‌بانو',
      'HokmMaster',
      'بردیا۷۷',
      'شاه‌مهره',
      'نازنین',
      'AceHunter',
      'کیانوش',
      'پریسا_۲۱',
    ])
    .toUint8Array();
}

const FIXTURE_PROFILE: ProfileModel = {
  username: FIXTURE_PLAYER.username,
  usernameReadonly: true,
  cardHolderName: 'سارا احمدی',
  cardHolderNameReadonly: false,
  email: 'sara@example.com',
  emailReadonly: true,
  mobile: '۰۹۱۲***۴۵۶۷',
  mobileReadonly: true,
  shaba: 'IR۸۲۰۵۴۰۱۰۲۶۸۰۰۲۰۸۱۷۹۰۹۰۰۱',
  shabaReadonly: false,
  cardNumber: '۶۰۳۷-۹۹۱۸-۱۲۳۴-۵۶۷۸',
  cardNumberReadonly: false,
  accountNumber: '۰۱۰۲۶۸۰۰۲۰۸۱۷۹',
  accountNumberReadonly: false,
  bankName: 'بانک پارسیان',
  bankNameReadonly: false,
};

function profileFrame(): Uint8Array {
  const writer = beginMessage(MessageTag.GetProfile);
  writeProfileModel(writer, FIXTURE_PROFILE);
  return writer.toUint8Array();
}

function cashFrame(): Uint8Array {
  return beginMessage(MessageTag.Cash)
    .putLong(12_750_000n)
    .putBool(true)
    .putString('ACE-۷۲۴۵')
    .putBool(true)
    .putLong(850_000n)
    .toUint8Array();
}

function agentListFrame(): Uint8Array {
  return beginMessage(MessageTag.AgentList)
    .putInt(4)
    .putStringArray([
      'ماه‌بانو · موجودی ۲.۴M',
      'بردیا۷۷ · موجودی ۹۵۰K',
      'کیانوش · موجودی ۱.۱M',
      'پریسا_۲۱ · موجودی ۷۸۰K',
    ])
    .toUint8Array();
}

function transactionListFrame(): Uint8Array {
  return beginMessage(MessageTag.TransactionList)
    .putStringArray([
      'افزایش موجودی · ۵۰۰,۰۰۰ تومان · موفق',
      'برداشت · ۸۵۰,۰۰۰ تومان · در انتظار',
      'جایزه بازی حکم · ۱۲۰,۰۰۰ تومان',
    ])
    .toUint8Array();
}

interface FixtureOption {
  text: string;
  value: number;
  minPrice: number;
}

function writeOptions(writer: BinaryWriter, options: readonly FixtureOption[]): void {
  writer.putInt(options.length);
  for (const option of options) {
    writer.putString(option.text).putShort(option.value).putInt(option.minPrice);
  }
}

function newGameConfigFrame(gameName: GameName): Uint8Array {
  const countOptions: FixtureOption[] = gameName === GameName.Hokm
    ? [
        { text: '۱ دست', value: 1, minPrice: 250_000 },
        { text: '۳ دست', value: 3, minPrice: 600_000 },
        { text: '۵ دست', value: 5, minPrice: 1_000_000 },
        { text: '۷ دست', value: 7, minPrice: 1_500_000 },
      ]
    : gameName === GameName.Backgammon
      ? [
          { text: '۱ دست', value: 1, minPrice: 300_000 },
          { text: '۲ دست', value: 2, minPrice: 550_000 },
          { text: '۳ دست', value: 3, minPrice: 800_000 },
        ]
      : [
          { text: '۱K', value: 1, minPrice: 100_000 },
          { text: '۳K', value: 3, minPrice: 300_000 },
          { text: '۵K', value: 5, minPrice: 500_000 },
          { text: '۱۰K', value: 10, minPrice: 1_000_000 },
          { text: '۲۰K', value: 20, minPrice: 2_000_000 },
        ];
  const typeOptions: FixtureOption[] = gameName === GameName.Hokm
    ? [
        { text: 'استاندارد', value: 0, minPrice: 0 },
        { text: 'نرس / سرس', value: 1, minPrice: 0 },
      ]
    : gameName === GameName.Backgammon
      ? [{ text: 'استاندارد', value: 0, minPrice: 0 }]
      : [{ text: 'ایرانی', value: 0, minPrice: 0 }];
  const timeOptions: FixtureOption[] = [
    { text: 'با آرامش', value: GameTime.Relax, minPrice: 0 },
    { text: 'نرمال', value: GameTime.Normal, minPrice: 0 },
    { text: 'سریع', value: GameTime.Fast, minPrice: 0 },
  ];

  const writer = beginMessage(MessageTag.GetNewGameConfigs)
    .putString(GameName[gameName])
    .putString(gameName === GameName.Blackjack ? 'قیمت هر بت' : 'تعداد بازی')
    .putString('نوع بازی')
    .putString('زمان بازی');
  writeOptions(writer, countOptions);
  writeOptions(writer, typeOptions);
  writeOptions(writer, timeOptions);
  writer.putInt(0).putInt(0).putInt(1);
  return writer
    .putLong(BigInt(countOptions[0].minPrice))
    .putInt(10_000_000)
    .putInt(100_000)
    .putInt(50_000)
    .toUint8Array();
}

function game(
  uniqueId: string,
  gameName: GameName,
  gameState: GameState,
  gamePrice: bigint,
  gameCount: number,
  gameType = 0,
): GameInfoModel {
  return {
    uniqueId,
    gameName,
    gameState,
    gameTime: GameTime.Normal,
    gameWaitState: gameState === GameState.Wait ? GameWaitState.Created : GameWaitState.WaitForConfirm,
    gamePrice,
    gameCount,
    gameType,
  };
}

interface FixtureSearchEntry {
  game: GameInfoModel;
  player1: PlayerModel;
  player2?: PlayerModel;
}

const SEARCH_ENTRIES: FixtureSearchEntry[] = [
  {
    game: game('hokm-1042', GameName.Hokm, GameState.Wait, 500_000n, 3),
    player1: { userId: 1_042n, username: 'شاه‌مهره', avatar: 4 },
  },
  {
    game: game('nard-2118', GameName.Backgammon, GameState.Running, 750_000n, 2),
    player1: { userId: 2_118n, username: 'کیانوش', avatar: 18 },
    player2: { userId: 2_119n, username: 'پریسا_۲۱', avatar: 24 },
  },
  {
    game: game('bj-3177', GameName.Blackjack, GameState.Wait, 1_200_000n, 10),
    player1: { userId: 3_177n, username: 'AceHunter', avatar: 11 },
  },
  {
    game: game('hokm-4190', GameName.Hokm, GameState.Running, 2_500_000n, 5, 1),
    player1: { userId: 4_190n, username: 'ماه‌بانو', avatar: 9 },
    player2: { userId: 4_191n, username: 'بردیا۷۷', avatar: 15 },
  },
];

function searchGameFrame(empty: boolean): Uint8Array {
  const entries = empty ? [] : SEARCH_ENTRIES;
  const writer = beginMessage(MessageTag.SearchGame).putInt(entries.length);
  for (const entry of entries) {
    writeGameInfoModel(writer, entry.game);
    writePlayerModel(writer, entry.player1);
    if (entry.player2) writePlayerModel(writer, entry.player2);
  }
  return writer.toUint8Array();
}

function readyFrame(isOwner: boolean): Uint8Array {
  const writer = beginMessage(isOwner ? MessageTag.IsReady : MessageTag.WaitReady);
  writeGameInfoModel(
    writer,
    {
      ...game('ready-hokm-7245', GameName.Hokm, GameState.Wait, 1_500_000n, 5),
      gameWaitState: GameWaitState.WaitForConfirm,
    },
  );
  writePlayerModel(writer, FIXTURE_PLAYER);
  writePlayerModel(writer, FIXTURE_OPPONENT);
  return writer
    .putShort(12)
    .putShort(9)
    .putShort(isOwner ? Role.Player1 : Role.Player2)
    .toUint8Array();
}

const TICKET_GAMES = [
  'حکم · برد · ۱۴۰۵/۰۴/۲۴ · میز 1042',
  'بیست و یک · باخت · ۱۴۰۵/۰۴/۲۳ · میز 3177',
];
const TICKET_TRANSACTIONS = [
  'خرید سکه · ۵۰۰,۰۰۰ تومان · موفق',
  'خرید سکه · ۱,۰۰۰,۰۰۰ تومان · ناموفق',
];
const TICKET_PAYBACKS = ['برداشت · ۸۵۰,۰۰۰ تومان · در انتظار'];

function ticketFrame(detail: boolean): Uint8Array {
  const writer = beginMessage(MessageTag.Ticket).putShort(detail ? TicketStatus.Done : TicketStatus.Empty);
  if (detail) {
    writer
      .putString('پیگیری برداشت وجه')
      .putString('درخواست برداشت من از دیروز در وضعیت انتظار باقی مانده است.')
      .putString('درخواست شما بررسی شد و تا پایان امروز به حساب بانکی ثبت‌شده واریز می‌شود.');
  }
  writer
    .putStringArray(TICKET_GAMES)
    .putStringArray(TICKET_TRANSACTIONS)
    .putStringArray(TICKET_PAYBACKS)
    .putUShort(5)
    .putLong(1_042n)
    .putLong(3_177n)
    .putLong(8_001n)
    .putLong(8_002n)
    .putLong(9_001n);
  return writer.toUint8Array();
}

function serverMessageFrame(message: string): Uint8Array {
  return beginMessage(MessageTag.SendMessage).putString(message).toUint8Array();
}
